import React, { useEffect, useRef, useState } from 'react';
import http from 'src/services/httpService';
import Button from 'src/componentes/button';
import Dialog from 'src/componentes/dialog';
import { FormattedMessage, useIntl } from 'react-intl';
import LoadingContainer from 'src/componentes/loading-container';
import Checkbox from 'src/componentes/checkbox';
import errorHandler from 'src/utils/error-handler';
import { useSnackbar } from 'notistack';
import { Row, Col } from 'reactstrap';
import useAuth from 'src/hooks/useAuth';
import LucideIcon from 'src/componentes/lucide-icon';
import ValidacaoRestricaoLista from 'src/componentes/validacao-restricao-lista/ValidacaoRestricaoLista';

export default function ValidarRestricao({
  url,
  urlExclusao,
  item,
  title = '',
  description = '',
  subDescription = '',
  atualizar = () => {},
  onClose = () => {},
  afterRemove = () => {},
  urlList = '',
  handleClose = () => {},
  exibirEstouCiente = true,
  labelCancelar = 'labelManterVinculo',
  labelAlterar = 'labelExcluirVinculo',
  width = 700,
  minHeight = 500,
  customHandleSalvarClick = null,
  customHandleCloseValidarRestricaoClick = null,
  restricoesModel = null,
  ...rest
}) {
  const { terms: resources } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [model, setModel] = useState(null || restricoesModel);
  const [estouCiente, setEstouCiente] = useState(false);
  const intl = useIntl();
  const modalRef = useRef();
  const exclusaoEmAndamentoRef = useRef(false);
  const { enqueueSnackbar } = useSnackbar();

  const handleCloseValidarRestricao = () => {
    if (customHandleCloseValidarRestricaoClick) {
      customHandleCloseValidarRestricaoClick();
    }

    if (modalRef.current) modalRef.current.close();

    handleClose && handleClose();
  };

  const handleSalvarClick = async (registro) => {
    if (customHandleSalvarClick) {
      customHandleSalvarClick();

      if (modalRef.current) modalRef.current.close();

      handleClose && handleClose();

      return;
    }

    if (urlExclusao) {
      if (exclusaoEmAndamentoRef.current) return;

      exclusaoEmAndamentoRef.current = true;
      setIsLoading(true);
      await http
        .post(urlExclusao, {
          id: registro.id,
        })
        .then(() => {
          setIsLoading(false);
          enqueueSnackbar(
            intl.formatMessage({ id: 'label.registroExcluidoComSucesso' }),
            {
              variant: 'success',
            }
          );
          afterRemove && afterRemove(registro);
          onClose && onClose(true);
          handleCloseValidarRestricao(true);
        })
        .catch((error) => {
          errorHandler(error);
          setIsLoading(false);
        });
      exclusaoEmAndamentoRef.current = false;
    }
    setIsLoading(false);
    atualizar && atualizar(handleCloseValidarRestricao());
    if (urlList) {
      window.location.href = urlList;
    }
  };

  const actions = [
    <Button
      key={1}
      data-testid="validar-restricao-cancel-button"
      onClick={() => handleCloseValidarRestricao()}
      color="secondary"
    >
      <FormattedMessage id={labelCancelar} />
    </Button>,
    <Button
      key={2}
      data-testid="validar-restricao-confirm-button"
      disabled={isLoading || (exibirEstouCiente && !estouCiente)}
      color="danger"
      className="ml-2"
      onClick={() => handleSalvarClick(item)}
    >
      <FormattedMessage id={labelAlterar} />
    </Button>,
  ];

  useEffect(() => {
    if (url && model == null) {
      setIsLoading(true);

      const parametros = { id: item.id };
      http
        .post(url, parametros)
        .then((response) => {
          setModel(response.data);
          setIsLoading(false);
        })
        .catch((error) => {
          errorHandler(error);
          setIsLoading(false);
        });
    }
    setIsLoading(false);
  }, [url, item]);

  return (
    <Dialog
      ref={modalRef}
      actions={actions}
      width={width}
      minHeight={minHeight}
      padContent
      onRequestClose={handleCloseValidarRestricao}
      mouseDownClose={false}
      {...rest}
    >
      <LoadingContainer isLoading={isLoading}>
        <div>
          <Row>
            <Col className="d-flex justify-content-center" md={2}>
              <div
                style={{
                  width: '56px', // Largura de 100px
                  height: '56px', // Altura igual à largura para formar o círculo
                  borderRadius: '50%', // Transforma em círculo
                  backgroundColor: 'red',
                  background: '#FFEBEB',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <LucideIcon icon="AlertTriangle" size={30} color="red" />
              </div>
            </Col>
            <Col>
              <div
                style={{
                  fontFamily: "'Nunito', sans-serif",
                  fontSize: '20px',
                  color: '#A90F0F',
                }}
              >
                <span>
                  {intl.formatMessage(
                    { id: title },
                    {
                      pontodecontrole: resources.pontodecontrole,
                      pontosdecontrole: resources.pontosdecontrole,
                      revisaodecontrole: resources.revisaodecontrole,
                      revisoesdecontrole: resources.revisoesdecontrole,
                      risco: resources.risco,
                      riscos: resources.riscos,
                      avaliacoes: resources.avaliacoes,
                      cicloavaliacao: resources.cicloavaliacao,
                    }
                  )}
                </span>
              </div>
              {(model?.description || description) && (
                <div className="pt-2">
                  <span
                    style={{
                      fontFamily: "'Nunito', sans-serif",
                      fontSize: '16px',
                    }}
                  >
                    {model?.description
                      ? model.description
                      : intl.formatMessage(
                          { id: description },
                          {
                            pontodecontrole: resources.pontodecontrole,
                            pontosdecontrole: resources.pontosdecontrole,
                            revisaodecontrole: resources.revisaodecontrole,
                            revisoesdecontrole: resources.revisoesdecontrole,
                            risco: resources.risco,
                            riscos: resources.riscos,
                            avaliacoes: resources.avaliacoes,
                            cicloavaliacao: resources.cicloavaliacao,
                          }
                        )}
                  </span>
                </div>
              )}
              {subDescription && (
                <div className="pt-2">
                  <span
                    style={{
                      fontFamily: "'Nunito', sans-serif",
                      fontSize: '16px',
                    }}
                  >
                    {intl.formatMessage({ id: subDescription })}
                  </span>
                </div>
              )}
              <div className="pt-3">
                <div className="tw:border tw:border-gray-200 tw:rounded-[5px] tw:shadow-[0_1px_2px_rgb(0_0_0/0.04),0_2px_8px_-2px_rgb(0_0_0/0.08)]">
                  <div className="tw:mt-4">
                    <ValidacaoRestricaoLista
                      restricoes={model?.restricoes || []}
                    />
                  </div>
                </div>
                {exibirEstouCiente &&
                  (model?.podeExcluir === true ||
                    model?.podeExcluir === undefined) && (
                    <div className="pt-3 d-flex align-items-center">
                      <Checkbox
                        data-testid="validar-restricao-awareness-checkbox"
                        style={{ marginBottom: 10 }}
                        model={{
                          value: estouCiente,
                          requestChange: setEstouCiente,
                        }}
                        color="primary"
                      />
                      <span
                        style={{
                          fontFamily: "'Nunito', sans-serif",
                          fontSize: '16px',
                        }}
                      >
                        {intl.formatMessage({
                          id: 'cienteAcaoIrreversivel',
                        })}
                      </span>
                    </div>
                  )}
              </div>
            </Col>
          </Row>
        </div>
      </LoadingContainer>
    </Dialog>
  );
}
